import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FiPlus, FiSearch } from 'react-icons/fi';

/**
 * "Search or create" combobox — a search input that reveals a results dropdown
 * when focused, with a final "Create new …" entry when the typed text doesn't
 * match an existing candidate.
 *
 * Reusable across the app — see `AGENTS.md` "UI patterns: search or create"
 * for the canonical usage notes.
 *
 * Behavior:
 * - The dropdown only appears while the input is focused (or the query is
 *   non-empty after a recent interaction). Outside-click dismisses it.
 * - The dropdown is absolutely positioned so it overlays surrounding content
 *   instead of pushing layout.
 * - Picking an existing candidate or "Create new" clears the input and
 *   dismisses the dropdown. Callers can therefore wire `onCreateNew` to open
 *   a pre-populated modal without worrying about the picker state.
 */
export interface PickerCandidate {
  id: string;
  name: string;
}

interface Props {
  candidates: PickerCandidate[];
  onPickExisting: (id: string) => void | Promise<void>;
  /** Called with the trimmed query when the user chooses to create. The
   *  picker clears itself before invoking this — callers should open a
   *  pre-populated form. */
  onCreateNew: (name: string) => void;
  /** Label for the "create" affordance, e.g. "Create new resource". */
  createLabel: string;
  placeholder?: string;
  /** Auto-focus the input on mount. Defaults to false. */
  autoFocus?: boolean;
  /** Render with smaller paddings + a capped result list height. */
  compact?: boolean;
}

const EntitySearchAndCreate: React.FC<Props> = ({
  candidates,
  onPickExisting,
  onCreateNew,
  createLabel,
  placeholder = 'Search or type a new name…',
  autoFocus = false,
  compact = false,
}) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(autoFocus);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Outside-click dismisses the dropdown.
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => c.name.toLowerCase().includes(q));
  }, [candidates, query]);

  const trimmedQuery = query.trim();
  const exactMatch = useMemo(
    () =>
      !!trimmedQuery &&
      candidates.some((c) => c.name.toLowerCase() === trimmedQuery.toLowerCase()),
    [candidates, trimmedQuery],
  );

  const reset = () => {
    setQuery('');
    setFocused(false);
  };

  const handlePickExisting = (id: string) => {
    reset();
    onPickExisting(id);
  };

  const handleCreate = () => {
    if (!trimmedQuery) return;
    const name = trimmedQuery;
    reset();
    onCreateNew(name);
  };

  const showDropdown = focused;
  const showCreateAffordance = !!trimmedQuery && !exactMatch;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cm-text-muted pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={placeholder}
          className={`w-full pl-9 pr-3 ${compact ? 'py-1.5' : 'py-2'} bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:border-cm-accent-orange focus:ring-cm-accent-orange focus:outline-none`}
        />
      </div>

      {showDropdown && (filtered.length > 0 || showCreateAffordance) && (
        <ul
          className={`absolute z-20 left-0 right-0 mt-1 bg-cm-bg-surface border border-cm-border-mid rounded-lg shadow-lg overflow-hidden ${
            compact ? 'max-h-48 overflow-y-auto' : 'max-h-64 overflow-y-auto'
          }`}
        >
          {filtered.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => handlePickExisting(c.id)}
                className="w-full px-3 py-2 text-sm text-left text-cm-text-primary hover:bg-cm-bg-hover transition-colors"
              >
                {c.name}
              </button>
            </li>
          ))}

          {showCreateAffordance && (
            <li className={filtered.length > 0 ? 'border-t border-cm-border-subtle' : ''}>
              <button
                type="button"
                onClick={handleCreate}
                className="w-full px-3 py-2 text-sm text-left text-cm-accent-blue hover:bg-cm-bg-hover transition-colors flex items-center gap-2"
              >
                <FiPlus className="w-3.5 h-3.5" />
                <span>
                  {createLabel}: <span className="font-medium">"{trimmedQuery}"</span>
                </span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default EntitySearchAndCreate;
